import React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { CONTENT_DEFAULT_BRANCH, RevisionPageType } from '@gitbook/hive-core';
import { useHiveBranchById, useHiveWorkingBranch } from '@gitbook/hive-react';
import { PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Badge,
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    FieldGroup,
    Fieldset,
    Group,
    Heading,
    HiddenDisclosureSection,
    Label,
    LoadingPanel,
    toast,
    useDialogClose,
} from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { assert, type MaybePromise } from '@gitbook/util-ts';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiClient } from '../../state';
import { encodeDocumentForAPI, useBasicEditorValue } from '../BasicEditor';
import { useBillingUsageAddon } from '../Billing';
import { SpaceIcon, useSelectSpacePalette } from '../Space';
import { TranslationInstructionInput } from './TranslationInstructionInput';
import { TranslationLanguagePicker } from './TranslationLanguagePicker';
import { TranslationPriceEstimate } from './TranslationPriceEstimate';

type CreateTranslationDialogResult = {
    translation: api.Translation;
    sourceSpace: api.Space;
};

interface CreateTranslationDialogContentProps
    extends Pick<CreateTranslationFormProps, 'defaultSpace' | 'currentSpace'> {
    organizationId: string;
    analyticsContext?: analytics.AnalyticsContextSpaceContent;
    /**
     * Describe the intent in which this dialog is used (space creation, etc.)
     */
    intent: analytics.TranslationAnalyticsIntent;

    /**
     * Callback to call when the translation is created.
     */
    onSubmit?: (result: CreateTranslationDialogResult) => MaybePromise<void>;
}

export type CreateTranslationDialogProps = CreateTranslationDialogContentProps;

/**
 * Dialog to create a new translation.
 */
export function CreateTranslationDialog(props: CreateTranslationDialogProps) {
    return (
        <Dialog>
            <CreateContentSourceTranslationDialogContent {...props} />
        </Dialog>
    );
}

function CreateContentSourceTranslationDialogContent(props: CreateTranslationDialogContentProps) {
    const { organizationId, onSubmit, analyticsContext, intent, currentSpace, defaultSpace } =
        props;
    const closeDialog = useDialogClose();

    useSetAnalyticsScreen({
        screen: 'translation_create_dialog',
        intent,
        ...analyticsContext,
    });

    return (
        <>
            <DialogHeader className="w-screen max-w-xl">
                <div className="flex flex-row items-center gap-2">
                    <Heading>Create translation</Heading>
                    <Badge kind="beta" label="Beta" />
                </div>
                <Description>
                    Automatically translate content from a source space using AI. The translation
                    will be kept in sync with the source content.
                </Description>
            </DialogHeader>
            <React.Suspense fallback={<LoadingPanel size="large" />}>
                <CreateTranslationForm
                    defaultSpace={defaultSpace}
                    currentSpace={currentSpace}
                    organizationId={organizationId}
                    onSubmit={async (result) => {
                        analytics.logEvent({
                            type: 'translation_create',
                            organization: organizationId,
                            sourceLanguage: result.translation.source.language,
                            targetLanguage: result.translation.language,
                            intent,
                        });

                        await onSubmit?.(result);
                        closeDialog();
                    }}
                />
            </React.Suspense>
        </>
    );
}

interface CreateTranslationFormProps {
    organizationId: string;
    onSubmit: (result: CreateTranslationDialogResult) => MaybePromise<void>;
    /**
     * Default space selected when the form is initialized.
     */
    defaultSpace?: api.Space;

    /**
     * Current space to exclude from the source space selection.
     */
    currentSpace?: api.Space;
}

/**
 * Form wrapped in the dialog to create a new translation.
 */
function CreateTranslationForm(props: CreateTranslationFormProps) {
    const { organizationId, onSubmit, defaultSpace, currentSpace } = props;

    const [addonDialog, addon] = useBillingUsageAddon({
        organizationId,
        meterName: api.BillingMeter.TranslationWords,
    });

    const [value, setValue] = React.useState<{
        sourceSpace: api.Space | null;
        sourceLanguage: api.TranslationLanguage | null;
        targetLanguage: api.TranslationLanguage | null;
    }>({
        sourceSpace: defaultSpace ?? null,
        sourceLanguage: api.TranslationLanguage.En,
        targetLanguage: null,
    });
    const instructions = useBasicEditorValue();

    const spaceSelectionPalette = useSelectSpacePalette({
        organizationId,
        filterSpace: currentSpace ? (space) => space.id !== currentSpace.id : undefined,
        onSpaceSelected: async (space) => {
            setValue((prevValue) => ({
                ...value,
                sourceSpace: space,
                sourceLanguage: space.language ?? prevValue.sourceLanguage,
            }));
        },
    });

    const [hasDocumentPages, setHasDocumentPages] = useStateWithDeps<boolean | null>(null, [
        value.sourceSpace?.id,
    ]);

    const disabled =
        !value.sourceSpace ||
        !value.sourceLanguage ||
        !value.targetLanguage ||
        value.sourceLanguage === value.targetLanguage ||
        hasDocumentPages === false ||
        hasDocumentPages === null;

    const closeDialog = useDialogClose();
    const onPressSubmit = async () => {
        await addon.withAddon(async () => {
            await toast.catch(async () => {
                assert(value.sourceSpace);
                assert(value.sourceLanguage);
                assert(value.targetLanguage);

                if (disabled) {
                    return;
                }
                const { data: translation } = await apiClient.translations.createTranslation(
                    organizationId,
                    {
                        source: {
                            space: value.sourceSpace.id,
                            language: value.sourceLanguage,
                        },
                        language: value.targetLanguage,
                        instructions: encodeDocumentForAPI(instructions.value.document),
                    }
                );

                await onSubmit({
                    translation,
                    sourceSpace: value.sourceSpace,
                });
            }, 'Error while creating translation');
        });
    };

    return (
        <>
            {addonDialog}
            <DialogBody>
                <Fieldset>
                    <FieldGroup orientation="horizontal">
                        <Group>
                            <Label>Source</Label>
                            <Description>
                                Select a space to use as the source for the content.
                            </Description>
                        </Group>
                        <PaletteButton
                            label={value.sourceSpace?.title ?? 'Select source'}
                            kind="secondary"
                            leadingIcon={
                                value.sourceSpace ? <SpaceIcon space={value.sourceSpace} /> : null
                            }
                            trailingIcon={Icon.ChevronDown}
                            palette={spaceSelectionPalette}
                        />
                    </FieldGroup>
                    <div className="flex flex-row justify-between gap-6">
                        <FieldGroup orientation="vertical">
                            <Group>
                                <Label>From</Label>
                            </Group>
                            <TranslationLanguagePicker
                                value={value.sourceLanguage}
                                onChange={(newValue) =>
                                    setValue({ ...value, sourceLanguage: newValue })
                                }
                            />
                        </FieldGroup>
                        <div className="flex items-end pb-1.5">
                            <Icon.ArrowRight className="size-5 text-muted" />
                        </div>
                        <FieldGroup orientation="vertical">
                            <Group>
                                <Label>To</Label>
                            </Group>
                            <TranslationLanguagePicker
                                value={value.targetLanguage}
                                onChange={(newValue) =>
                                    setValue({ ...value, targetLanguage: newValue })
                                }
                                skip={value.sourceLanguage}
                            />
                        </FieldGroup>
                    </div>
                    {value.sourceSpace ? (
                        <React.Suspense fallback={null}>
                            <SourceSpaceDocumentPagesWarning
                                spaceId={value.sourceSpace.id}
                                onResult={setHasDocumentPages}
                            />
                        </React.Suspense>
                    ) : null}
                    {hasDocumentPages === false || !value.sourceSpace?.id ? null : (
                        <TranslationPriceEstimate
                            organizationId={organizationId}
                            spaceId={value.sourceSpace?.id}
                        />
                    )}
                    <HiddenDisclosureSection
                        labels={{
                            show: 'Show advanced instructions',
                            hide: 'Hide advanced instructions',
                        }}
                    >
                        <TranslationInstructionInput
                            value={instructions.value}
                            onChange={instructions.setValue}
                        />
                    </HiddenDisclosureSection>
                </Fieldset>
            </DialogBody>
            <DialogFooter>
                <Button kind="secondary" label="Cancel" onClick={closeDialog} />
                <Button label="Create" disabled={disabled} onPress={onPressSubmit} />
            </DialogFooter>
        </>
    );
}

/**
 * Warning to show when the source space has no pages to translate.
 */
function SourceSpaceDocumentPagesWarning(props: {
    spaceId: string;
    onResult: (hasDocumentPages: boolean) => void;
}) {
    const { spaceId, onResult } = props;

    const branch = useHiveBranchById({ contentId: spaceId, branchId: CONTENT_DEFAULT_BRANCH });
    const { sourceRevision } = useHiveWorkingBranch(branch);

    const { hasOnlyTranslation, hasDocumentPages } = React.useMemo(() => {
        const pages = Object.values(sourceRevision.pages);
        const hasDocumentPages =
            pages.length > 0 && pages.some((page) => page.type === RevisionPageType.Document);

        const hasOnlyTranslation =
            !hasDocumentPages &&
            pages.some(
                (page) =>
                    page.type === RevisionPageType.Computed &&
                    page.computed.type === 'builtin:translation'
            );

        return { hasDocumentPages, hasOnlyTranslation };
    }, [sourceRevision]);

    React.useEffect(() => {
        onResult(hasDocumentPages);
    }, [hasDocumentPages, onResult]);

    if (hasDocumentPages) {
        return null;
    }

    return (
        <Alert
            size="xsmall"
            kind="warning"
            details={
                hasOnlyTranslation
                    ? 'The selected source space is already a translation. You cannot create a translation of a translation. Use the original source space instead.'
                    : 'The selected source space has no pages to translate.'
            }
        />
    );
}
